"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.submitScorecardLinkSchema = exports.SCORECARD_LINK_REFUSALS = exports.SCORECARD_LINK_DAYS = exports.SCORECARD_LINK_HEADER = exports.DRAFT_CONFLICT_CODE = exports.flagInterviewSchema = exports.waiveScorecardSchema = exports.correctScorecardSchema = exports.submitScorecardSchema = exports.saveScorecardDraftSchema = exports.scorecardDraftSchema = exports.OVERALL_MARK_LABELS = exports.SCORECARD_COMMENT_MAX = exports.OVERALL_MARK_MAX = exports.OVERALL_MARK_MIN = exports.criterionDraftSchema = exports.WAIVER_REASON_MAX = exports.CHANGE_NOTE_MAX = exports.CRITERION_COMMENT_MAX = exports.SCORECARD_NOTE_MAX = exports.DRAFT_EVENT_MINUTES = exports.HOURS_PER_WORKING_DAY_EQUIVALENT = exports.WAIVER_CHASE_LEVEL = exports.SCORECARD_CHASE = exports.INTERVIEW_FLAG_LABELS = exports.SCORECARD_STATUS_LABELS = exports.RECOMMENDATION_LABELS = void 0;
exports.isPassing = isPassing;
exports.submitProblems = submitProblems;
exports.emptyDraft = emptyDraft;
exports.alignDraft = alignDraft;
exports.scorecardLinkExpiry = scorecardLinkExpiry;
exports.scorecardLinkRefusal = scorecardLinkRefusal;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const schemas_1 = require("./schemas");
// S20 — the interview cockpit and scorecards (INT-01…03, F11.01–F11.11). Sources: master
// plan S20 and its planning decisions (DECISIONS-LOG S20 Q1–Q8); E9; §L3; §L4 as
// clarified in S20; §L22 N-31, N-37–N-40; §L23; §L25 scorecard.
/** F11.03's final call — four choices (S20 Q1). Both yeses count as a pass for §L4. */
exports.RECOMMENDATION_LABELS = {
    strong_yes: 'Strong yes',
    yes: 'Yes',
    no: 'No',
    strong_no: 'Strong no',
};
/** §L4 counts passes and fails; S20 Q1 maps the four choices onto them. */
function isPassing(recommendation) {
    return recommendation === 'strong_yes' || recommendation === 'yes';
}
exports.SCORECARD_STATUS_LABELS = {
    draft: 'Not submitted',
    submitted: 'Submitted',
    waived: 'Waived',
};
/** F11.10 — the structured quick-log flags. */
exports.INTERVIEW_FLAG_LABELS = {
    candidate_late: 'Candidate arrived late',
    cut_short: 'Interview cut short',
    technical_failure: 'Technical failure',
};
/**
 * §L4 as clarified in S20 Q4: where each rung of the chase sits, in working days after
 * the scorecard's due time (itself the round's deadline after the interview completed).
 */
exports.SCORECARD_CHASE = [
    { level: 1, afterDueWorkingDays: 0, to: 'panelist' },
    { level: 2, afterDueWorkingDays: 1, to: 'manager' },
    { level: 3, afterDueWorkingDays: 3, to: 'hr_lead' },
];
/** F11.11: the waiver is offered once the last rung (the HR Lead) has fired. */
exports.WAIVER_CHASE_LEVEL = 3;
/** Hours in a round's deadline that make one working day (S20 Q4: "each 24h = one working day"). */
exports.HOURS_PER_WORKING_DAY_EQUIVALENT = 24;
/** `scorecard.draft_saved` at most this often per scorecard — not per keystroke. */
exports.DRAFT_EVENT_MINUTES = 5;
// --- Inputs ------------------------------------------------------------------------------
exports.SCORECARD_NOTE_MAX = 5000;
exports.CRITERION_COMMENT_MAX = 2000;
exports.CHANGE_NOTE_MAX = 1000;
exports.WAIVER_REASON_MAX = 1000;
exports.criterionDraftSchema = zod_1.z.object({
    criterionId: schemas_1.uuidSchema,
    score: zod_1.z.number().int().nullable(),
    isNotAssessed: zod_1.z.boolean(),
    comment: zod_1.z.string().max(exports.CRITERION_COMMENT_MAX),
});
/** C5 (DECISIONS-LOG 2026-11-01 Q3): one overall mark, 1 to 5, and a short comment. */
exports.OVERALL_MARK_MIN = 1;
exports.OVERALL_MARK_MAX = 5;
exports.SCORECARD_COMMENT_MAX = 1000;
exports.OVERALL_MARK_LABELS = {
    1: 'Poor',
    2: 'Below what we need',
    3: 'Meets what we need',
    4: 'Strong',
    5: 'Exceptional',
};
/**
 * The author's working copy — what the cockpit edits and the device keeps (F11.05). Since C5 a
 * panelist fills `overallMark` and writes `notes` (the comment); `criteria` and `recommendation`
 * stay for scorecards filled before it and are empty on a new one.
 */
exports.scorecardDraftSchema = zod_1.z.object({
    criteria: zod_1.z.array(exports.criterionDraftSchema).max(100),
    recommendation: zod_1.z.enum(enums_1.RECOMMENDATIONS).nullable(),
    notes: zod_1.z.string().max(exports.SCORECARD_NOTE_MAX),
    overallMark: zod_1.z.number().int().min(exports.OVERALL_MARK_MIN).max(exports.OVERALL_MARK_MAX).nullable().default(null),
});
/**
 * F11.05 / F11.06. `baseRevision` is the server revision this device last saw: a save
 * from an older one is a conflict unless the author chose to keep this device's copy.
 */
exports.saveScorecardDraftSchema = zod_1.z.object({
    baseRevision: zod_1.z.number().int().min(0),
    draft: exports.scorecardDraftSchema,
    /** Queued while offline and sent on reconnect (`scorecard.synced`). */
    isOfflineSync: zod_1.z.boolean().default(false),
    /** "Keep this device's version" after a conflict prompt — local wins (F11.06). */
    isOverwrite: zod_1.z.boolean().default(false),
});
/** F11.07 and F11.08 — the draft travels with the submit, so a device that was offline submits what it shows. */
exports.submitScorecardSchema = exports.saveScorecardDraftSchema.extend({
    changeNote: zod_1.z.string().trim().max(exports.CHANGE_NOTE_MAX).nullable().default(null),
});
/** F11.08 by an HR Lead on someone else's scorecard: a new version, with the reason. */
exports.correctScorecardSchema = zod_1.z.object({
    basedOnVersion: zod_1.z.number().int().min(1),
    draft: exports.scorecardDraftSchema,
    changeNote: zod_1.z.string().trim().min(1, 'Say what you changed and why.').max(exports.CHANGE_NOTE_MAX),
});
/** F11.11 — reason mandatory (§L23). */
exports.waiveScorecardSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1, 'Say why this scorecard is being waived.').max(exports.WAIVER_REASON_MAX),
});
exports.flagInterviewSchema = zod_1.z.object({
    kind: zod_1.z.enum(enums_1.INTERVIEW_FLAG_KINDS),
    note: zod_1.z.string().trim().max(exports.CHANGE_NOTE_MAX).nullable().default(null),
});
// --- Rules -------------------------------------------------------------------------------
/**
 * F11.07's precondition as C5 sets it (ADR 0077): a counting panelist gives one overall mark and a
 * short comment; an observer (kept from before C5) writes a comment. Each problem in plain words,
 * so the Submit button can say why. The scoring matrix no longer makes a scorecard (decision 2).
 */
function submitProblems(draft, isObserver) {
    const problems = [];
    if (!isObserver && draft.overallMark === null)
        problems.push(`Give your overall mark, ${exports.OVERALL_MARK_MIN} to ${exports.OVERALL_MARK_MAX}.`);
    if (draft.notes.trim() === '')
        problems.push('Write a short comment.');
    if (draft.notes.length > exports.SCORECARD_COMMENT_MAX)
        problems.push(`Keep the comment under ${exports.SCORECARD_COMMENT_MAX} characters.`);
    return problems;
}
/** An empty working copy. */
function emptyDraft() {
    return { criteria: [], recommendation: null, notes: '', overallMark: null };
}
/** A draft lined up with the rubric: one entry per criterion, in order, whatever was stored. */
function alignDraft(draft, criteria) {
    return {
        ...draft,
        criteria: criteria.map((criterion) => draft.criteria.find((entry) => entry.criterionId === criterion.id) ?? { criterionId: criterion.id, score: null, isNotAssessed: false, comment: '' }),
    };
}
/**
 * F11.06: the error code of a save from an older revision, and what the server holds —
 * the cockpit asks which to keep, this device's being the default (local wins).
 */
exports.DRAFT_CONFLICT_CODE = 'draft_conflict';
// --- C6: the panelist's scorecard link (ADR 0077) ------------------------------------------
/** The link's token travels in this header, as the candidate's booking link does — never in an access log. */
exports.SCORECARD_LINK_HEADER = 'x-scorecard-link';
/** C6-b (DECISIONS-LOG 2026-11-01 Q5): a link works until its scorecard is submitted, or this long after the interview. */
exports.SCORECARD_LINK_DAYS = 7;
const DAY_MS = 86_400_000;
exports.SCORECARD_LINK_REFUSALS = {
    submitted: 'Your scorecard is in. Thank you — this link has done its job.',
    expired: `This link has expired. Links stop working ${exports.SCORECARD_LINK_DAYS} days after the interview. Ask HR if you still need to give your mark.`,
    revoked: 'A newer link was sent for this interview. Use the one in the latest email.',
    cancelled: 'This interview did not go ahead, so there is nothing to score.',
    off_panel: "You're no longer on this interview's panel.",
    decided: 'This round has been decided, so its scorecards can no longer change.',
    waived: 'HR waived this scorecard, so it can no longer change.',
};
/** When a link stops working: 7 days after the interview's end (its booked end, else when it was completed). */
function scorecardLinkExpiry(interviewEnd) {
    return interviewEnd ? new Date(interviewEnd.getTime() + exports.SCORECARD_LINK_DAYS * DAY_MS) : null;
}
/**
 * C6: whether one link opens its one scorecard now, and if not, why. Pure, so expiry, one use and
 * scope are unit-tested. The order matters: a submitted scorecard says "thank you", not "expired".
 */
function scorecardLinkRefusal(facts, now) {
    if (facts.usedAt || facts.scorecardStatus === 'submitted')
        return 'submitted';
    if (facts.revokedAt)
        return 'revoked';
    if (facts.scorecardStatus === 'waived')
        return 'waived';
    if (facts.isDecided)
        return 'decided';
    if (facts.interviewStatus === 'cancelled' || facts.interviewStatus === 'candidate_no_show')
        return 'cancelled';
    if (!facts.isOnPanel || facts.scorecardStatus === null)
        return 'off_panel';
    const expiresAt = scorecardLinkExpiry(facts.interviewEnd);
    if (facts.expiredAt || (expiresAt && now >= expiresAt))
        return 'expired';
    return null;
}
/** The link's one write: the mark and the comment, submitted together. */
exports.submitScorecardLinkSchema = zod_1.z.object({
    overallMark: zod_1.z
        .number()
        .int()
        .min(exports.OVERALL_MARK_MIN, `Marks run from ${exports.OVERALL_MARK_MIN} to ${exports.OVERALL_MARK_MAX}.`)
        .max(exports.OVERALL_MARK_MAX, `Marks run from ${exports.OVERALL_MARK_MIN} to ${exports.OVERALL_MARK_MAX}.`),
    comment: zod_1.z.string().trim().min(1, 'Write a short comment.').max(exports.SCORECARD_COMMENT_MAX, `Keep the comment under ${exports.SCORECARD_COMMENT_MAX} characters.`),
});
//# sourceMappingURL=scorecards.js.map